import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { advertisingGranted, measurementGranted } from '../analytics';
import { LegalMention } from '../components/legal';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, Divider, Field, MicrosoftButton, PasswordField } from '../components/ui';
import { ApiError, api } from '../services/api';
import { completeHandoff, useHandoff } from '../services/handoff';
import { useSession } from '../session';
import type { InvitationPreview, Locale } from '../types';

/** State handed over by the invitation page: the token and its preview (email prefilled, read-only). */
export interface SignupLocationState {
  invitation?: { token: string; preview: InvitationPreview };
}

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useSession();
  const invitation = (location.state as SignupLocationState | null)?.invitation;
  const handoff = useHandoff();
  const [email, setEmail] = useState(invitation?.preview.email ?? '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invitation) setEmail(invitation.preview.email);
  }, [invitation]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    setBusy(true);
    try {
      const data = await api.signup({ email: email.trim(), password, first_name: firstName.trim(), last_name: lastName.trim(), locale: i18n.language as Locale, invitation_token: invitation?.token });
      setUser(data.user);
      // The conversion, on a created account only and with the visitor's consent: the GA4 key event, and the LinkedIn
      // rule for an account created by a visitor (an invitation is not one). Written in full so that they can be read
      // in the built bundle. The add-in's dialog page replaces this one at once: the events go first, within 500 ms. GA4
      // says when it has sent; LinkedIn does not, so its conversion gets the whole 500 ms.
      const linkedIn = advertisingGranted() && !invitation && typeof window.lintrk === 'function';
      if (linkedIn) window.lintrk?.('track', { conversion_id: 31855170 });
      let sent = () => {};
      const delivered = new Promise<void>((resolve) => {
        sent = () => {
          if (!linkedIn) resolve();
        };
        setTimeout(() => resolve(), 500);
      });
      if (measurementGranted() && typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', { method: 'email', signup_type: invitation ? 'invitation' : 'direct', event_callback: () => sent() });
      } else {
        sent();
      }
      if (handoff) {
        await delivered;
        completeHandoff(handoff, data); // the add-in's dialog page takes it from here
        return;
      }
      navigate(invitation ? '/organization' : '/', { replace: true });
    } catch (err) {
      setError(signupError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || !email.trim() || !password;
  return (
    <PublicLayout title={t('signup.title')} subtitle={invitation ? undefined : t('signup.subtitle')}>
      {invitation && (
        <Alert kind="success">
          <strong>{t('signup.invited', { organization: invitation.preview.organization })}</strong>
          <div className="text-[12px]">{t('signup.invitedHint')}</div>
        </Alert>
      )}
      {handoff && <Alert kind="info">{t('handoff.notice')}</Alert>}
      {/* Once, above both buttons that create the account: Microsoft and the form's. */}
      <LegalMention />
      <MicrosoftButton href={handoff ? api.microsoftAddinUrl(handoff.origin) : api.microsoftLoginUrl(invitation ? `/invite/${invitation.token}` : '/')} label={t('auth.microsoft')} />
      <Divider label={t('auth.or')} />
      <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field id="first-name" label={t('auth.firstName')} autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Field id="last-name" label={t('auth.lastName')} autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Field id="email" label={t('auth.email')} type="email" autoComplete="username" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} readOnly={Boolean(invitation)} disabled={Boolean(invitation)} required />
        <PasswordField id="password" label={t('auth.password')} autoComplete="new-password" placeholder={t('auth.passwordPlaceholder')} hint={t('auth.passwordRule')} value={password} onChange={(e) => setPassword(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} required />
        {error && <Alert kind="error">{error}</Alert>}
        <Button type="submit" disabled={disabled} className="mt-1 w-full">
          {busy ? t('auth.creating') : t('auth.createAccount')}
        </Button>
      </form>
      <p className="text-center text-[13px] text-muted">
        {t('auth.haveAccount')}{' '}
        {/* A sign-in does not accept the invitation: it comes back to it, as the invitation page's own link does. */}
        <Link to="/login" state={invitation ? { returnTo: `/invite/${invitation.token}` } : location.state} className="text-accent underline hover:text-accent-dark">
          {t('auth.signIn')}
        </Link>
      </p>
    </PublicLayout>
  );
}

function signupError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    if (err.is('email_taken')) return t('auth.emailTaken');
    if (err.is('email_plus_address')) return t('auth.emailPlusAddress');
    if (err.is('email_disposable_domain')) return t('auth.emailDisposableDomain');
    if (err.is('weak_password')) return t('auth.weakPassword');
    if (err.is('bad_invitation')) return t('auth.badInvitation');
    if (err.is('invitation_mismatch')) return t('auth.invitationMismatch');
    if (err.is('rate_limited')) return t('app.rateLimited');
  }
  return t('app.error');
}
